import { BarChart, LineChart, MapChart, PieChart } from 'echarts/charts';
import { GeoComponent, GridComponent, LegendComponent, TooltipComponent, VisualMapComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import timezones from '../maps/timezones.json';

echarts.use([
  BarChart,
  LineChart,
  MapChart,
  PieChart,
  GeoComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

echarts.registerMap('timezones', timezones);

export default {
  Chart: {
    mounted() {
      this.chart = echarts.init(this.el);
      this.draw();

      this.observer = new ResizeObserver(() => this.chart?.resize());
      this.observer.observe(this.el);
    },

    updated() {
      this.draw();
    },

    destroyed() {
      this.observer?.disconnect();
      this.chart?.dispose();
      this.chart = undefined;
    },

    draw() {
      const raw = this.el.dataset.option;
      if (!raw || !this.chart) return;

      const option = JSON.parse(raw);
      applyDurationFormatters(option);
      applyVisitorFormatters(option);

      // A count of 1 is log(1) = 0, so it sits on the axis. Start below it and hide that tick.
      if (option.yAxis?.type === 'log') {
        option.yAxis.min = 0.1;
        option.yAxis.axisLabel = {
          ...option.yAxis.axisLabel,
          formatter: value => (value >= 1 ? String(value) : ''),
        };
        option.tooltip = {
          ...option.tooltip,
          valueFormatter: value => (value < 1 ? '0' : String(value)),
        };
      }

      this.chart.setOption(option, { notMerge: true });
    },
  },
};

function formatDuration(seconds) {
  const value = Math.max(0, Math.round(Number(seconds) || 0));
  if (value < 60) return `${value}s`;
  if (value < 3600) return `${Math.floor(value / 60)}m`;
  if (value < 86400) {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(value / 86400);
  const hours = Math.floor((value % 86400) / 3600);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}

function applyVisitorFormatters(option) {
  if (option.tooltip?.formatter !== 'visitors') return;

  option.tooltip.formatter = params => {
    const row = Array.isArray(params) ? params[0] : params;
    return `${row.name}<br/>${row.data?.visitors ?? row.value}`;
  };
}

function applyDurationFormatters(option) {
  if (option.yAxis?.axisLabel?.formatter === 'duration') {
    option.yAxis.axisLabel.formatter = formatDuration;
  }
  if (option.tooltip?.valueFormatter === 'duration') {
    option.tooltip.valueFormatter = formatDuration;
  }
  (option.series || []).forEach(series => {
    if (series.label?.formatter === 'duration') {
      series.label.formatter = params => formatDuration(params.value);
    }
  });
}
